import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api } from '../api/client'
import type { User } from '../api/types'

interface AuthContextValue {
  user: User | null
  loading: boolean
  demoLogin: () => Promise<void>
  requestCode: (channel: 'EMAIL' | 'PHONE', destination: string) => Promise<{ challengeId: string; developmentCode?: string }>
  verifyCode: (challengeId: string, code: string) => Promise<void>
  passwordLogin: (email: string, password: string) => Promise<void>
  requestPasswordReset: (email: string) => Promise<{ developmentToken?: string }>
  confirmPasswordReset: (token: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    api
      .get<{ authenticated: boolean; user?: User }>('/api/v1/auth/me')
      .then((session) => {
        if (active) setUser(session.authenticated ? session.user ?? null : null)
      })
      .catch(() => {
        if (active) setUser(null)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  const demoLogin = useCallback(async () => {
    const authenticated = await api.post<User>('/api/v1/auth/demo')
    setUser(authenticated)
  }, [])

  const requestCode = useCallback((channel: 'EMAIL' | 'PHONE', destination: string) => {
    return api.post<{ challengeId: string; developmentCode?: string }>('/api/v1/auth/request-code', { channel, destination })
  }, [])

  const verifyCode = useCallback(async (challengeId: string, code: string) => {
    const authenticated = await api.post<User>('/api/v1/auth/verify-code', { challengeId, code })
    setUser(authenticated)
  }, [])

  const passwordLogin = useCallback(async (email: string, password: string) => {
    const authenticated = await api.post<User>('/api/v1/auth/password-login', { email, password })
    setUser(authenticated)
  }, [])

  const requestPasswordReset = useCallback((email: string) => {
    return api.post<{ developmentToken?: string }>('/api/v1/auth/password-reset/request', { email })
  }, [])

  const confirmPasswordReset = useCallback((token: string, password: string) => {
    return api.post<void>('/api/v1/auth/password-reset/confirm', { token, password })
  }, [])

  const logout = useCallback(async () => {
    await api.post<void>('/api/v1/auth/logout')
    setUser(null)
  }, [])

  const value = useMemo(
    () => ({ user, loading, demoLogin, requestCode, verifyCode, passwordLogin, requestPasswordReset, confirmPasswordReset, logout }),
    [user, loading, demoLogin, requestCode, verifyCode, passwordLogin, requestPasswordReset, confirmPasswordReset, logout],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
